// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { observer } from "mobx-react-lite";
import { ChangeEvent, useEffect } from "react";

import { MiniLoader } from "../../../MiniLoader/MiniLoader";
import { useStore } from "../../../StoreProvider/StoreProvider";
import * as Styled from "../../CaseDetails.styles";
import {
  LSIR_SCORE_KEY,
  OFFENSE_KEY,
  SEX_OFFENSE_KEY,
  VIOLENT_OFFENSE_KEY,
} from "../../constants";
import { TextInput } from "../Elements/TextInput";
import { form } from "../FormStore";
import { FormFieldProps } from "../types";
import { useFormField } from "../useFormFields";
import { isValidLsirScore } from "../utils";

function LsirScoreField({ isRequired }: FormFieldProps) {
  const { PSIStore, geoConfig } = useStore();
  const caseAttributes = PSIStore.caseAttributes;
  const omsSystem = geoConfig.omsSystem;
  const insight = PSIStore.insight;
  const prevLsirScore =
    caseAttributes?.lsirScore === null ? "" : String(caseAttributes?.lsirScore);

  const currentOffense =
    form.updates[OFFENSE_KEY] ?? PSIStore.caseAttributes?.offense;
  const isCurrentOffenseViolentDefault =
    currentOffense !== undefined &&
    PSIStore.offensesByName[currentOffense]?.isViolentOffense;
  const isCurrentOffenseSexualDefault =
    currentOffense !== undefined &&
    PSIStore.offensesByName[currentOffense]?.isSexOffense;

  const { inputValue, setInputValue } = useFormField({
    initialInputValue: prevLsirScore,
  });

  const showRollup =
    !form.hasError &&
    inputValue !== "" &&
    insight &&
    insight.rollupOffenseName !== insight.offense;

  const rollupContent = !geoConfig.hideRecidivismRatesChart && (
    <>
      {PSIStore.insightLoading && (
        <Styled.RollupLoading>
          <MiniLoader dark /> Gathering historical records...
        </Styled.RollupLoading>
      )}

      {showRollup && !PSIStore.insightLoading && (
        <Styled.RollupOffenseCategory>
          <Styled.InputLabel>Recidivism Cohort</Styled.InputLabel>
          <span>{insight.rollupOffenseDescription}</span>
          <Styled.InputDescription>
            In order to provide recidivism rates based on a sufficient sample
            size, we need to broaden the group of similar cases we use to
            compare this case to. The new cohort may include all genders, risk
            scores, and/or a more general category of offense. A description of
            the cohort is listed above. If you think this categorization is
            inaccurate, reach out to Recidiviz.
          </Styled.InputDescription>
        </Styled.RollupOffenseCategory>
      )}
    </>
  );

  const updateLsirScore = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value.trim());
    form.updateForm(
      LSIR_SCORE_KEY,
      e.target.value.trim() ? Number(e.target.value) : null,
      isRequired,
      isValidLsirScore,
    );

    /** Fetches insights when LSI-R score changes to display rollup text under the LSI-R score field  */

    const isCurrentOffenseViolent =
      form.updates[VIOLENT_OFFENSE_KEY] ??
      (caseAttributes?.offense === currentOffense
        ? caseAttributes?.isCurrentOffenseViolent
        : isCurrentOffenseViolentDefault);
    const isCurrentOffenseSexual =
      form.updates[SEX_OFFENSE_KEY] ??
      (caseAttributes?.offense === currentOffense
        ? caseAttributes?.isCurrentOffenseSexual
        : isCurrentOffenseSexualDefault);

    PSIStore.getInsight(
      form.updates[OFFENSE_KEY] ?? PSIStore.caseAttributes.offense,
      form.hasError ? undefined : Number(e.target.value.trim()),
      isCurrentOffenseSexual,
      isCurrentOffenseViolent,
    );
  };

  /** Validate previously saved LSI-R score */
  useEffect(() => {
    form.validate(LSIR_SCORE_KEY, prevLsirScore, isRequired, isValidLsirScore);
    return () => form.resetErrors();
  }, [prevLsirScore, isRequired]);

  /** Fetch insights on previously saved values */
  useEffect(() => {
    if (!PSIStore.insightLoading) {
      PSIStore.getInsight(
        PSIStore.caseAttributes.offense,
        caseAttributes?.lsirScore ?? undefined,
        caseAttributes?.isCurrentOffenseSexual ?? isCurrentOffenseSexualDefault,
        caseAttributes?.isCurrentOffenseViolent ??
          isCurrentOffenseViolentDefault,
      );
    }
    // We only need to call this once on load to update/clear previously fetched insights
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Styled.InputLabel htmlFor={LSIR_SCORE_KEY}>
        LSI-R Score {isRequired && <span>Required*</span>}
      </Styled.InputLabel>

      <TextInput
        id={LSIR_SCORE_KEY}
        value={inputValue ?? ""}
        onChange={updateLsirScore}
        isDisabled={caseAttributes.isLsirScoreLocked}
        hasError={form.errors[LSIR_SCORE_KEY]?.inputError}
        errorMessage="Please enter a number between 0 and 54."
      />

      {!caseAttributes.isLsirScoreLocked && geoConfig.showLsirScoreHelperText && (
        <Styled.InputDescription>
          If this is a File Review or if the LSI-R has not yet been scored,
          enter the most recent risk score for this individual.
        </Styled.InputDescription>
      )}

      {caseAttributes.isLsirScoreLocked && (
        <Styled.InputDescription>
          This score has been pulled in from {omsSystem} and is unable to be
          edited.
        </Styled.InputDescription>
      )}

      {rollupContent}
    </>
  );
}

export default observer(LsirScoreField);
